import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { setUserContext, clearUserContext, captureError } from '@/lib/sentry';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string) => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, options?: { data?: Record<string, any> }) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    let mounted = true;
    
    const initializeAuth = async () => {
      try {
        console.log('[AuthContext] Initializing auth session');
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        
        if (mounted && session?.user) {
          console.log('[AuthContext] User found in session:', session.user.id);
          setUser(session.user);
          // If a pending invitation redirect is queued, honor it from any
          // entry point (login page or otherwise). Guard against open-redirect
          // by only honoring paths that target the invitation flow.
          const pendingInvitation = sessionStorage.getItem('pendingInvitationRedirect');
          if (pendingInvitation) {
            sessionStorage.removeItem('pendingInvitationRedirect');
            if (pendingInvitation.startsWith('/accept-invitation')) {
              console.log('[AuthContext] Redirecting to pending invitation:', pendingInvitation);
              navigate(pendingInvitation);
            } else if (location.pathname === '/login') {
              navigate('/calendar');
            }
          } else if (location.pathname === '/login') {
            console.log('[AuthContext] Redirecting to calendar from login page');
            navigate('/calendar');
          }
        } else {
          console.log('[AuthContext] No authenticated user found');
        }
      } catch (error) {
        console.error('[AuthContext] Error in initializeAuth:', error);
      } finally {
        if (mounted) {
          console.log('[AuthContext] Auth initialization complete');
          setLoading(false);
        }
      }
    };

    initializeAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      console.log('[AuthContext] Auth state changed:', event);
      setUser(session?.user ?? null);
      
      if (event === 'SIGNED_IN' && session?.user) {
        console.log('[AuthContext] User signed in:', session.user.id);
        // Set user context for error tracking
        setUserContext({ id: session.user.id, email: session.user.email });
        // Honor a queued invitation redirect from any entry point. Only paths
        // that begin with /accept-invitation are accepted to prevent
        // open-redirect via a poisoned sessionStorage value.
        const pendingInvitation = sessionStorage.getItem('pendingInvitationRedirect');
        if (pendingInvitation) {
          sessionStorage.removeItem('pendingInvitationRedirect');
          if (pendingInvitation.startsWith('/accept-invitation')) {
            console.log('[AuthContext] Redirecting to pending invitation:', pendingInvitation);
            navigate(pendingInvitation);
          } else if (location.pathname === '/login') {
            navigate('/calendar');
          }
        } else if (location.pathname === '/login') {
          console.log('[AuthContext] Redirecting to calendar from login page');
          navigate('/calendar');
        }
      } else if (event === 'SIGNED_OUT') {
        console.log('[AuthContext] User signed out');
        clearUserContext();
        // Reset workspace resolution state so the next sign-in is treated
        // as a fresh sign-in (default flag wins) rather than a continuation.
        sessionStorage.removeItem('workspaceResolvedSession');
        navigate('/login');
      }
      
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, location.pathname]);

  const signIn = async (email: string) => {
    if (!email.trim()) {
      toast({
        title: "Error",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('[AuthContext] Sending magic link to:', email);
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) throw error;

      console.log('[AuthContext] Magic link sent successfully');
      toast({
        title: "Check your email",
        description: "We've sent you a magic link to sign in.",
      });
    } catch (error: any) {
      console.error('[AuthContext] Sign in error:', error);

      if (error.status === 429) {
        toast({
          title: "Too many attempts",
          description: "Please wait a few minutes before trying again.",
          variant: "destructive",
        });
        return;
      }

      captureError(error as Error, { feature: 'auth-magic-link' });
      toast({
        title: "Error",
        description: error.message || "Failed to send magic link. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const signInWithPassword = async (email: string, password: string) => {
    try {
      console.log('[AuthContext] Attempting password sign in for:', email);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      console.log('[AuthContext] Password sign in successful');
      toast({
        description: "Successfully signed in",
      });
    } catch (error: any) {
      console.error('[AuthContext] Password sign in error:', error);
      captureError(error as Error, { feature: 'auth-password-signin' });
      toast({
        title: "Error",
        description: error.message || "Invalid email or password",
        variant: "destructive",
      });
      throw error;
    }
  };

  const signUp = async (email: string, password: string, options?: { data?: Record<string, any> }) => {
    if (!email.trim()) {
      toast({
        title: "Error",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('[AuthContext] Attempting to sign up:', email);
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: options?.data,
          emailRedirectTo: `${window.location.origin}/auth/callback`
        },
      });
      
      if (error) throw error;

      console.log('[AuthContext] Sign up successful');
      toast({
        title: "Check your email",
        description: "We've sent you a verification link to complete your registration.",
      });
    } catch (error: any) {
      console.error('[AuthContext] Sign up error:', error);

      if (error.message?.includes('already registered')) {
        toast({
          title: "Account exists",
          description: "An account with this email already exists. Please sign in instead.",
          variant: "destructive",
        });
        return;
      }

      captureError(error as Error, { feature: 'auth-signup' });
      toast({
        title: "Error",
        description: error.message || "Failed to create account. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      console.log('[AuthContext] Signing out user');
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      console.log('[AuthContext] User signed out successfully');
      navigate('/login');
    } catch (error: any) {
      console.error('[AuthContext] Sign out error:', error);
      captureError(error as Error, { feature: 'auth-signout' });
      toast({
        title: "Error",
        description: error.message || "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      signIn, 
      signInWithPassword,
      signUp, 
      signOut 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}